import { Button } from '@/shared/components/ui/button'
import { Typography } from '@/shared/components/ui/typography'
import { cn } from '@/shared/lib/utils'
import {
  IRecommendationItem,
  RecommendationsListState,
} from '@/types/recommendations'
import ErrorOutlined from '@mui/icons-material/ErrorOutlined'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import { ComponentProps, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { LearningRecommendationCard } from './LearningRecommendationCard'

const NUMBER_OF_COLUMNS = 2
const BORDER_COLOR = '#E2E3E6' // Tailwind 'border' color
const DEFAULT_ACCENT_COLOR = '#5E636F' // Tailwind 'mydra-gray' color

type LearningRecommendationsListProps = Omit<
  ComponentProps<'section'>,
  'title'
> & {
  title: React.ReactNode
  htmlTitle?: string
  ctaButton: {
    href: string
    label: React.ReactNode
  }
  items: IRecommendationItem[]
  ranked?: boolean
  accentColors: string[]
  maxItems: number
  state?: RecommendationsListState
  onRetry?: () => Promise<void>
}

export function LearningRecommendationsList({
  title,
  htmlTitle,
  ctaButton,
  items,
  accentColors,
  state = RecommendationsListState.Idle,
  ranked,
  maxItems = 10,
  onRetry,
  ...props
}: LearningRecommendationsListProps) {
  const [isRetrying, setIsRetrying] = useState(false)
  const numberOfRows = Math.round(maxItems / NUMBER_OF_COLUMNS)
  const isLoading = state === RecommendationsListState.Loading
  const isError = state === RecommendationsListState.Error
  const isScheduled = state === RecommendationsListState.Scheduled

  const handleRetry = async () => {
    if (!onRetry || isRetrying) return

    setIsRetrying(true)
    try {
      await onRetry()
    } catch {
      //ignore
    } finally {
      setIsRetrying(false)
    }
  }

  useEffect(() => {
    if (isLoading) {
      setIsRetrying(false)
    }
  }, [isLoading])

  return (
    <section
      {...props}
      title={htmlTitle}
      className={cn(
        'flex flex-col overflow-hidden rounded-t-lg',
        props.className
      )}
    >
      <header
        style={
          {
            '--line-colors': `linear-gradient(to right, ${
              Array.isArray(accentColors) && accentColors?.length
                ? accentColors.join(', ')
                : DEFAULT_ACCENT_COLOR
            }`,
          } as React.CSSProperties
        }
        className="relative flex flex-col items-center justify-between gap-4 bg-grey-01 px-4 py-6 after:absolute after:bottom-0 after:left-0 after:z-10 after:h-1 after:w-full after:bg-[image:var(--line-colors)] after:content-[''] md:flex-row md:px-4 md:py-8"
      >
        <Typography
          variant="subtitle"
          className="flex-1 text-center font-semibold max-md:text-lg md:text-left"
        >
          {title}
        </Typography>
        {ctaButton?.href && ctaButton?.label && (
          <>
            <Button size="sm" asChild className="md:hidden">
              <Link to={ctaButton.href} target="_blank" rel="noreferrer">
                {ctaButton.label}
              </Link>
            </Button>
            <Button asChild className="hidden md:block">
              <Link to={ctaButton.href} target="_blank" rel="noreferrer">
                {ctaButton.label}
              </Link>
            </Button>
          </>
        )}
      </header>
      {isError ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-6 border border-t-0 border-error-light bg-error-light/20 bg-paper px-4 py-16 md:rounded-b-lg">
          <div className="flex flex-col items-center gap-2 text-center">
            <div className="flex items-center gap-1 text-error-main">
              <ErrorOutlined />
              <Typography variant="subtitle" className="font-semibold">
                Error loading courses.
              </Typography>
            </div>
            <Typography
              variant="body1"
              className="font-semibold text-mydra-gray"
            >
              Refresh the page or contact support if the issue continues
            </Typography>
          </div>
          {onRetry && (
            <Button
              size="sm"
              onClick={handleRetry}
              variant="default"
              disabled={isRetrying}
            >
              {isRetrying ? 'Retrying...' : 'Retry'}
            </Button>
          )}
        </div>
      ) : isScheduled ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-1 border border-t-0 bg-grey-01 px-4 py-16 text-center md:rounded-b-lg">
          <div className="flex items-center gap-1">
            <HourglassEmptyIcon />
            <Typography variant="subtitle" className="font-semibold">
              This might take a couple of minutes.
            </Typography>
          </div>
          <Typography variant="body2" className="">
            We&apos;re currently generating your course recommendations —
            this&apos;ll just take a moment.
          </Typography>
        </div>
      ) : (
        <div
          style={
            {
              '--number-of-columns': `repeat(${NUMBER_OF_COLUMNS}, 1fr)`,
              '--number-of-rows': `repeat(${numberOfRows}, 1fr)`,
            } as React.CSSProperties
          }
          className="grid flex-1 overflow-hidden border md:grid-flow-col md:grid-cols-[--number-of-columns] md:grid-rows-[--number-of-rows] md:rounded-b-lg"
        >
          {isLoading
            ? Array.from({ length: maxItems }, (_, index) => (
                <div
                  key={`loading-${index}`}
                  style={{
                    borderBottom:
                      (index + 1) % numberOfRows === 0
                        ? 'none'
                        : `1px solid ${BORDER_COLOR}`,
                    borderRight:
                      index >= numberOfRows
                        ? 'none'
                        : `1px solid ${BORDER_COLOR}`,
                  }}
                  className="h-full max-md:!border-r-0 max-md:last:!border-b-0"
                >
                  <LearningRecommendationCard
                    prefix={ranked ? `${index + 1}` : ''}
                    className="h-full !rounded-none !border-0"
                    isLoading
                  />
                </div>
              ))
            : (items ?? []).slice(0, maxItems).map((item, index) => {
                const rowIndex = index % numberOfRows
                const isEvenRow = rowIndex % 2 === 0
                return (
                  <div
                    key={item._id}
                    style={{
                      borderBottom:
                        (index + 1) % numberOfRows === 0
                          ? 'none'
                          : `1px solid ${BORDER_COLOR}`,
                      borderRight:
                        index >= numberOfRows
                          ? 'none'
                          : `1px solid ${BORDER_COLOR}`,
                    }}
                    className={cn(
                      'h-full max-md:!border-r-0 max-md:last:!border-b-0',
                      'md:data-[even-row=true]:bg-grey-02'
                    )}
                    data-even-row={isEvenRow}
                  >
                    <LearningRecommendationCard
                      prefix={ranked ? `${index + 1}` : ''}
                      className="h-full !rounded-none !border-0"
                      item={item}
                    />
                  </div>
                )
              })}
        </div>
      )}
    </section>
  )
}
